import {
  isAgedBetween18And99,
  validateNinWithSpaceStrict,
} from "@storebrand-digital/storebrand-common-validation";
import {
  AlertTitle,
  Button,
} from "Components/TextContext/commonComponentEnums";
import { Form, Formik, FormikProps } from "formik";

import { StyledAlert } from "App/global-style";
import { AlertType } from "Components/Form/Alert";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { NinInput } from "Components/Form/NinInput";
import { useTexts } from "Components/TextContext";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { buttonNextTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import React from "react";
import { validationSchema } from "./loginFormValidationSchema";
import { NoLoginFormValues } from "./loginFormValues";

export interface PreviousButtonProps {
  readonly previousButton?: {
    readonly onClick: () => void;
  };
}
interface Props extends PreviousButtonProps {
  readonly onSubmit: (formValues: NoLoginFormValues) => void;
  readonly isLoading: boolean;
  readonly validNameNin?: boolean;
  readonly submitButtonTrackId?: string;
}
export const LoginForm = ({
  onSubmit,
  isLoading,
  validNameNin = true,
  submitButtonTrackId,
  previousButton = undefined,
}: Props): React.ReactElement => {
  const { getText } = useTexts();
  const submitTrackId = isDefined(submitButtonTrackId)
    ? submitButtonTrackId
    : buttonNextTrackId("ssn-login");

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        nin: "",
      }}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={validationSchema()}
    >
      {(formProps: FormikProps<NoLoginFormValues>) => (
        <Form className="data-hj-suppress">
          <Input
            large
            id="firstName"
            name="firstName"
            label={getText("customerform.label.firstname")}
            error={getError("firstName", formProps)}
            inputMode={InputMode.TEXT}
          />
          <Input
            large
            id="lastName"
            name="lastName"
            label={getText("customerform.label.lastname")}
            error={getError("lastName", formProps)}
            inputMode={InputMode.TEXT}
          />
          <NinInput
            large
            id="nin"
            name="nin"
            label={getText("customerform.label.nin")}
            hint={getText("customerform.hint.nin")}
            error={getError("nin", formProps)}
            inputProps={{ autoComplete: "off" }}
          />
          {!validNameNin && (
            <StyledAlert
              description={getText(
                "checkoutforms.alertdescription.customernamevalidationerror",
              )}
              type="error"
              fluid
              title={getText(AlertTitle.SOMETHING_WENT_WRONG)}
              alertId="mismatchNameNin"
              alertType={AlertType.STOPPER_ALERT}
            />
          )}
          {formProps.values.nin &&
            validateNinWithSpaceStrict(formProps.values.nin).valid &&
            !isAgedBetween18And99(formProps.values.nin) && (
              <StyledAlert
                description={getText("customerform.validation.nin-ageinvalid")}
                type="error"
                fluid
                title={getText(AlertTitle.SOMETHING_WENT_WRONG)}
                alertId="ageInvalid"
                alertType={AlertType.STOPPER_ALERT}
              />
            )}
          <ThemedButton
            trackId={submitTrackId}
            type="submit"
            disabled={
              isLoading ||
              (!!formProps.values.nin &&
                !isAgedBetween18And99(formProps.values.nin))
            }
            loading={isLoading}
          >
            {getText(Button.NEXT)}
          </ThemedButton>
          {isDefined(previousButton) && (
            <ThemedButton
              trackId="ssn-previous"
              type="button"
              variant="outlined"
              style={{ marginLeft: 8 }}
              disabled={
                isLoading ||
                (!!formProps.values.nin &&
                  !isAgedBetween18And99(formProps.values.nin))
              }
              onClick={previousButton.onClick}
            >
              Tilbake
            </ThemedButton>
          )}
        </Form>
      )}
    </Formik>
  );
};
